import { useCallback, useMemo, useState } from 'react';
import { v4 } from 'uuid';
import { SportType } from '../../models/court/dto/court.dto';
import {
    CLEARED_CELL,
    GridSlot,
    PriceOverrides,
    PricingBlock,
    PricingGridState,
    flattenBlocksToSlots,
    makeDefaultBlock,
    makeNextBlock,
    priceOverrideKey,
} from './model';

export function makeInitialGridState(): PricingGridState {
    return { blocks: [makeDefaultBlock(v4())], overrides: {} };
}

/** Local state for the grid editor, with the handful of edits the UI makes. */
export function usePricingGridState(initial?: PricingGridState) {
    const [state, setState] = useState<PricingGridState>(() => initial ?? makeInitialGridState());
    const slots = useMemo(() => flattenBlocksToSlots(state.blocks), [state.blocks]);

    const updateBlock = useCallback((blockId: string, patch: Partial<PricingBlock>) => {
        setState((s) => ({ ...s, blocks: s.blocks.map((b) => (b.id === blockId ? { ...b, ...patch } : b)) }));
    }, []);

    const addBlock = useCallback(() => {
        setState((s) => ({ ...s, blocks: [...s.blocks, makeNextBlock(s.blocks, v4())] }));
    }, []);

    const removeBlock = useCallback((blockId: string) => {
        setState((s) => (s.blocks.length <= 1 ? s : { ...s, blocks: s.blocks.filter((b) => b.id !== blockId) }));
    }, []);

    /** Set (or clear, with `null`) the price of `targetSlots` on `day` for `sport`. */
    const applyPrice = useCallback((sport: SportType, day: number, targetSlots: GridSlot[], price: number | null) => {
        setState((s) => {
            const overrides: PriceOverrides = { ...s.overrides };
            for (const slot of targetSlots) {
                overrides[priceOverrideKey(sport, day, slot)] = price === null ? CLEARED_CELL : price;
            }
            return { ...s, overrides };
        });
    }, []);

    return { state, setState, slots, updateBlock, addBlock, removeBlock, applyPrice };
}
